import { Locale, localeRuleset } from '../ruleset/locale';
import { PetStatusNotification } from '../petChatbot';
import type { GuestNotificationId } from '../guest/guest.chatbot';

export type CreativeText = {
  id: string;
  cta: string;
  text: string;
};

export type CreativeTextWithoutCTA = {
  id: string;
  text: string;
};

type ReplicantTextConfig = {
  [lang: string]: {
    status: Record<PetStatusNotification, { text: string }>;
    energy: Record<'full', { text: string }>;
    visit: Record<'finish' | 'revived' | 'backToLife', { text: string }>;
    duo: Record<
      'invite' | 'expiration' | 'bubble' | 'response' | 'reminder',
      { text: string }
    >;
    joined: Record<'default', { text: string }>;
    card: Record<'received' | 'request', { text: string }>;
    daycare: Record<'finish', { text: string }>;
    reengagement: Record<'d7' | 'd14' | 'd28', { text: string }>;
    purchase: Record<'pending', { text: string }>;
    competitive: Record<
      | 'started'
      | 'endsIn'
      | 'points'
      | 'rank'
      | 'rankUndefined'
      | 'friendsRank'
      | 'friendsRankUndefined'
      | 'ended'
      | 'reminder'
      | 'gachaReminder',
      { text: string }
    >;
    gift: Record<'bPoints' | 'count', { text: string }>;
    friendshipRequest: { text: string };
    friendRequestAccepted: { text: string };
    resurrect: Record<'altAll' | 'playNow' | 'revive', { text: string }>;
    dailyBonus: { altText: string };
    spawnPet: Record<'onFire' | 'open' | 'reveal', { text: string }>;
    buddies: {
      invitation: { text: string };
      nudge: { title: string; body: string };
    };
    liveOps: Record<
      | 'fuji'
      | 'lampsStart'
      | 'lampsEnd'
      | 'matchLife'
      | 'waterballoon'
      | 'moveLikeBuruburu'
      | 'lollipopWednesday'
      | 'streakChampion'
      | 'yellowFestival'
      | 'miniFirecracker'
      | 'turtleRace'
      | 'dreamDrift'
      | 'tournamentStart'
      | 'tournamentSoon'
      | 'tournamentTomorrow'
      | 'tournamentEnd',
      { text: string }
    >;
    inviteGreeting: {
      messages: Record<
        'morning' | 'lunch' | 'evening',
        CreativeTextWithoutCTA[]
      >;
      share: CreativeTextWithoutCTA[];
      buttons: {
        yes: string;
        no: string;
      };
    };
    crossplay: { text: string };
    retention: string;
    weeklyLeaderboard: {
      '28h': { title: string; body: string };
      '4h': { title: string; body: string };
    };
    guest: Record<GuestNotificationId, { title: string; body: string }>;
  };
};

function getConfig(): ReplicantTextConfig {
  return {
    en: {
      card: {
        received: { text: 'You received a card!' },
        request: { text: 'Your friend needs a card.' },
      },
      status: {
        dirty: { text: 'Maybe I should take a bath soon.' },
        hungry: { text: 'I’m a little bit hungry now…' },
        lonely: { text: 'I‘m feeling lonely. Will you come visit me?' },
        pee: { text: 'I think I need to go to the bathroom.' },
        evolving: { text: 'I feel like my body is getting all tingly…' },
        scared: { text: 'I’m scared… Help me…' },
        sick: { text: 'I feel sick. Please help me' },
        tooth_decay: { text: 'My teeth hurt. What is this?' },
        very_dirty: { text: 'I‘m itchy. I want to take a bath!' },
        very_hungry: { text: 'I’m starving…' },
        very_lonely: {
          text: 'I’m super lonely. Can you come and play with me?',
        },
        very_pee: { text: 'I can’t hold it anymore… Bathroom…' },
        dead: { text: 'Will you come to see me?' },
        dying: { text: 'Help me...' },
        want_to_go_out: { text: 'I want to go out!' },
      },
      energy: {
        full: { text: 'Energy is fully charged!' },
      },
      visit: {
        finish: { text: 'I just played with {name}!' },
        revived: { text: `I've revived your pet!` },
        backToLife: { text: 'Yay! I’m back to life!' },
      },
      duo: {
        invite: { text: '{name} just\ninvited you to form a duo!' },
        reminder: { text: '{name} is\nwaiting for your response!' },
        response: { text: '{name} is responding!' },
        expiration: { text: 'Please respond within {hh} hours {mm} minutes!' },
        bubble: { text: 'I took it seriously' },
      },
      joined: {
        default: { text: 'A friend just started the game!' },
      },
      daycare: {
        finish: { text: 'Daycare is over!' },
      },
      reengagement: {
        d7: { text: 'Want to play together again?' },
        d14: { text: 'Will you come back?' },
        d28: { text: "You don't like us anymore?" },
      },
      purchase: {
        pending: { text: 'Finish your purchase!' },
      },
      competitive: {
        started: { text: 'A new Gacha Event has Started!' },
        endsIn: { text: 'Until yyyy-M-D h:mm' },
        reminder: { text: 'Event latest status' },
        gachaReminder: { text: 'Gacha Event ending soon!' },
        ended: { text: 'Event ended!' },
        points: { text: 'Event point: {N}Pt' },
        rank: { text: 'Global ranking: {N}' },
        rankUndefined: { text: 'Global ranking: --' },
        friendsRank: { text: 'Friend ranking: {N}' },
        friendsRankUndefined: { text: 'Friend ranking: --' },
      },
      gift: {
        bPoints: { text: '{name} sent you a gift!' },
        count: { text: 'You have {count} gifts waiting to collect' },
      },
      friendshipRequest: {
        text: '{name} has sent you a friend request',
      },
      friendRequestAccepted: {
        text: '{name} has accepted your Friend Request',
      },
      resurrect: {
        altAll: {
          text: 'Open ASAP!',
        },
        playNow: {
          text: 'Play Now!',
        },
        revive: {
          text: 'Log in to the game now,\nBuruBuru might come back to life!',
        },
      },
      dailyBonus: {
        altText: 'Daily Bonus! 🎁',
      },
      spawnPet: {
        onFire: { text: 'House on fire tap to douse' },
        open: { text: 'Open your pet crate' },
        reveal: { text: 'Tap to find which pet you got' },
      },
      buddies: {
        invitation: { text: 'Join the baking party!' },
        nudge: {
          title: 'Baking Buddies',
          body: 'Your buddy needs your help! Come bake together!',
        },
      },
      liveOps: {
        fuji: { text: 'Ending Soon! ⏳' },
        lampsStart: { text: 'Magic Lamps Start🪔🪔' },
        lampsEnd: { text: 'Magic Lamps will end🌟🌟' },
        matchLife: { text: 'Match Life🎉🎉' },
        waterballoon: { text: 'Water Balloon Splash💦💦' },
        moveLikeBuruburu: { text: 'Move like Buruburu🧩✨' },
        lollipopWednesday: { text: 'Lollipop Wednesday🍭🍭' },
        streakChampion: { text: 'BuruStreak Champion🥇🥇' },
        yellowFestival: { text: 'Yellow Festival🟨🟨' },
        miniFirecracker: { text: 'Mini Firecracker Festival🎆🎆' },
        turtleRace: { text: 'Turtle Race! 🐢🏁' },
        dreamDrift: { text: 'Dream Drift' },
        tournamentStart: { text: 'Tournament Start👑👑' },
        tournamentSoon: { text: 'Tournament will End⏳⏳' },
        tournamentTomorrow: { text: 'Tournament will End Tomorrow🌟🌟' },
        tournamentEnd: { text: 'Tournament Ended🏆🏆' },
      },
      inviteGreeting: {
        messages: {
          morning: [
            {
              id: 'direct_morning_1',
              text: `Good morning... What are you doing today? I'm feeling lonely being all by myself. I'd be 	happy if you could bring some friends to the game...`,
            },
            {
              id: 'direct_morning_2',
              text: `Morning... What are you excited about today? I want to play too, but I'm always lonely. Could you bring some friends over?`,
            },
            {
              id: 'direct_morning_3',
              text: `Good morning... What's your plan for today? I'm really lonely being all by myself. Could you bring some friends to the game?`,
            },
          ],
          lunch: [
            {
              id: 'direct_afternoon_1',
              text: `Hey... What did you do this afternoon? I've been lonely all by myself. Could you bring some friends to the game for me?`,
            },
            {
              id: 'direct_afternoon_2',
              text: `Hello... How was your afternoon? I've been so lonely that I feel like crying. I'd be happy if you could bring some friends to the game...`,
            },
            {
              id: 'direct_afternoon_3',
              text: `Hello... How was your afternoon? I've been so lonely that I feel like crying. I'd be happy if you could bring some friends to the game...`,
            },
          ],
          evening: [
            {
              id: 'direct_evening_1',
              text: `Good evening... How was your day? I've been alone all day. So, I'd be happy if you could bring some friends to the game for me...`,
            },
            {
              id: 'direct_evening_2',
              text: `Good job today... What are you doing tonight? I'm very lonely being all by myself. Could you bring some friends to the game?`,
            },
            {
              id: 'direct_evening_3',
              text: `Good evening... How was your day? I've been lonely all day, so I'd be happy if you could bring some friends to the game..`,
            },
          ],
        },
        share: [
          {
            id: 'default',
            text: 'My pet feels lonely without friends…\nIf you have time, please try playing Buruburu Dobutsu',
          },
        ],
        buttons: {
          yes: 'Sure!',
          no: 'No way',
        },
      },
      crossplay: { text: "Let's complete your purchase!" },
      retention: 'Log in to the game now,\nBuruBuru might come back to life!',

      weeklyLeaderboard: {
        '28h': {
          title: '🚨 1 Day Left to Win Big!',
          body: 'The clock is ticking—boost your rank now and claim your exclusive rewards before it’s too late! 🎁⏳',
        },
        '4h': {
          title: '⏳ 4H Left! Finish Strong!',
          body: 'The leaderboard resets soon—don’t lose your spot! Play now! 🎮🔥',
        },
      },

      guest: {
        dailyBonus: {
          title: 'Daily Bonus',
          body: '🎁 Your Daily Bonus is here—claim it now!',
        },
        tournamentLive: {
          title: 'Tournament',
          body: '🏆 Tournament time! Show your skills and rise to the top',
        },
        tournamentOngoing: {
          title: 'Tournament',
          body: '🔥 Your spot’s at risk! Defend your place in the tournament',
        },
        magicLampsStart: {
          title: 'Magic Lamps',
          body: '🪔 Magic Lamps are live! Choose wisely, win big.',
        },
        magicLampsEnd: {
          title: 'Magic Lamps',
          body: '🪔 Ending soon! Rub a lamp before the magic fades!',
        },
        treasureExpressLive: {
          title: 'Treasure Express',
          body: '🚆 All aboard Treasure Express—more wins, more prizes!',
        },
        treasureExpressOngoing: {
          title: 'Treasure Express',
          body: '⏳ Treasure Express continues—don’t stop now!',
        },
        buruLunch: {
          title: 'Buruburu',
          body: '🍎 Your Buru misses you! Come say hi during lunch!',
        },
        buruEvening: {
          title: 'Buruburu',
          body: '🌙 Your Buru is getting sleepy… Come tuck it in!',
        },
        dreamDrift: {
          title: 'Dream Drift',
          body: 'Choose your hero and clear puzzles to earn rewards!',
        },
        weeklyLeaderboards28h: {
          title: '🚨 1 Day Left to Win Big!',
          body: 'The clock is ticking—boost your rank now and claim your exclusive rewards before it’s too late! 🎁⏳',
        },
        weeklyLeaderboards4h: {
          title: '⏳ 4H Left! Finish Strong!',
          body: 'The leaderboard resets soon—don’t lose your spot! Play now! 🎮🔥',
        },
      },
    },
    ja: {
      card: {
        received: { text: 'カードを受け取りました！' },
        request: { text: 'お友達がカードを必要としています。' },
      },
      status: {
        dirty: { text: 'そろそろお風呂入りたいかも…' },
        hungry: { text: 'ちょっとおなか空いてきたなあ…。' },
        lonely: { text: 'なんだかさみしくなっちゃった。遊びに来て？' },
        pee: { text: 'そろそろトイレに行きたいかも…' },
        evolving: { text: 'なんだか、からだがムズムズする…' },
        scared: { text: 'こわいー。こわいー。たすけてー' },
        sick: { text: 'うーん…。くるしい…。たすけて…' },
        tooth_decay: { text: '歯がいたいよー。なにこれー？' },
        very_dirty: { text: 'かゆいよー。お風呂入りたいー' },
        very_hungry: { text: 'ひもじいよー。死んじゃうよー' },
        very_lonely: { text: 'さーみーしーいー…。きて…？' },
        very_pee: { text: 'もぅ…限界…。トイレ…' },
        dead: { text: 'さいごに会いに来てほしいな' },
        dying: { text: 'たすけて…死んじゃう…' },
        want_to_go_out: { text: 'おでかけしたいよ〜' },
      },
      energy: {
        full: { text: 'エネルギー満タンだよ！' },
      },
      visit: {
        finish: { text: '{name}\nとあそんだよ！' },
        revived: { text: 'ぶるぶるを生き返らせたよ！' },
        backToLife: { text: 'やったー！生き返ったよ！' },
      },

      duo: {
        invite: { text: '{name}から\nコンビ結成を申し込まれたよ！' },
        reminder: { text: '{name} が\nお返事を待ってるよ！' },
        response: { text: '{name}が\nお返事しますって！' },
        expiration: { text: '{hh}時間{mm}分以内にお返事してね' },
        bubble: { text: '真剣に考えました' },
      },
      joined: {
        default: { text: '友だちがゲームを始めたよ！' },
      },
      daycare: {
        finish: { text: '保育園おわったよ！' },
      },
      reengagement: {
        d7: { text: 'またいっしょにあそぼ？' },
        d14: { text: 'また来てくれるよね？' },
        d28: { text: 'もう嫌いになっちゃった？' },
      },
      purchase: {
        pending: { text: '購入を完了しよう！' },
      },
      competitive: {
        started: { text: 'イベントスタート！' },
        endsIn: { text: 'yyyy年M月D日 h時mm分まで' },
        reminder: { text: 'イベント現状報告' },
        gachaReminder: { text: 'イベントガチャ終了間近！' },
        ended: { text: 'イベント終了〜' },
        points: { text: 'イベントポイント: {N}点' },
        rank: { text: '全体ランキング: 第{N}位' },
        rankUndefined: { text: '全体ランキング: 圏外' },
        friendsRank: { text: '友だちランキング: 第{N}位' },
        friendsRankUndefined: { text: '友だちランキング: 圏外' },
      },
      gift: {
        bPoints: { text: '{name}が\nプレゼントを送ってくれたよ' },
        count: { text: '{count}個のプレゼントが待っています' },
      },
      friendshipRequest: {
        text: '{name}が\nフレンド申請を送ってくれたよ',
      },
      friendRequestAccepted: {
        text: '{name}が\nフレンド申請を承認しました',
      },
      resurrect: {
        altAll: {
          text: '緊急です！',
        },
        playNow: {
          text: '今すぐプレイ！',
        },
        revive: {
          text: '今ゲームをプレイすると\nぶるぶるが生き返るかも！',
        },
      },
      dailyBonus: {
        altText: 'デイリーボーナス！🎁',
      },
      spawnPet: {
        onFire: { text: 'タップして消化しに行こう！' },
        open: { text: 'ぶるぶるを確認してみよう！' },
        reveal: { text: 'タップしてぶるぶるを見つけよう！' },
      },
      buddies: {
        invitation: { text: 'ベーキングパーティーに参加しよう！' },
        nudge: {
          title: 'ベーキングバディーズ',
          body: 'バディが助けを求めています！一緒にベーキングしよう！',
        },
      },
      liveOps: {
        fuji: { text: 'もうすぐ終わっちゃう！⏳' },
        lampsStart: { text: '魔法のランプがスタート🪔🪔' },
        lampsEnd: { text: '魔法のランプが終わっちゃうよ🌟🌟' },
        matchLife: { text: '今日はパズル祭り🎉🎉' },
        waterballoon: { text: '今日は水雨風船祭り💦💦' },
        moveLikeBuruburu: { text: '今日は残り手数祭り🧩✨' },
        lollipopWednesday: { text: '今日はロリポップ祭り🍭🍭' },
        streakChampion: { text: '今日は連続クリア祭り🥇🥇' },
        yellowFestival: { text: '今日は黄色祭り🟨🟨' },
        miniFirecracker: { text: '今日は花火祭り！🎆🎆' },
        turtleRace: { text: 'カメさんレース！🐢🏁' },
        dreamDrift: { text: 'おねんねヒーロー' },
        tournamentStart: { text: 'トーナメント開始！👑👑' },
        tournamentSoon: { text: 'もうすぐ終わっちゃう！⏳⏳' },
        tournamentTomorrow: { text: 'トーナメントは明日まで🌟🌟' },
        tournamentEnd: { text: 'トーナメント終了🏆🏆' },
      },
      inviteGreeting: {
        messages: {
          morning: [
            {
              id: 'direct_morning_1',
              text: `おはよう…今日は何するの？僕、ひとりぼっちで寂しいんだ。友だちをゲームに連れてきてくれたら嬉しいな…`,
            },
            {
              id: 'direct_morning_2',
              text: `おっはー…。今日は何が楽しみ？僕も一緒に遊びたいけど、いつもひとりぼっちで寂しいんだ…。友だち連れてきてくれないかな？`,
            },
            {
              id: 'direct_morning_3',
              text: `おはおは…今日はどんな予定？僕、ひとりぼっちでとても寂しいんだ。友だちをゲームに連れてきてくれる？`,
            },
          ],
          lunch: [
            {
              id: 'direct_afternoon_1',
              text: `やっほー…。午後は何してた？僕、ずっとひとりぼっちで寂しいんだよ…。僕のために友だちをゲームに連れてきてくれる？`,
            },
            {
              id: 'direct_afternoon_2',
              text: `こんにちは…。今日の午後はどうだった？僕、ずっとひとりぼっちで寂しくて泣きそうだよ…。友だちをゲームに連れてきてくれると嬉しいな…`,
            },
            {
              id: 'direct_afternoon_3',
              text: `こんにちは…。午後も頑張ってる？僕、ひとりぼっちでとても寂しいんだ…。友だちをゲームに連れてきてほしいな…`,
            },
          ],
          evening: [
            {
              id: 'direct_evening_1',
              text: `こんばんは…。今日はどんな一日だった？僕、実はずっとひとりぼっちなんだ…。だから、僕のために友だちをゲームに連れてきてくれると嬉しいな…`,
            },
            {
              id: 'direct_evening_2',
              text: `お疲れ様…。夜は何するの？僕、ひとりぼっちでとても寂しいんだ…。友だちをゲームに連れてきてくれる？`,
            },
            {
              id: 'direct_evening_3',
              text: `こんばんは…。今日はどうだった？僕、ずっとひとりぼっちで寂しいから、友だちをゲームに連れてきてくれると嬉しいな…`,
            },
          ],
        },

        share: [
          {
            id: 'default',
            text: 'うちの子が友達がいなくて寂しいみたい...\nよければ、ぶるぶるどーぶつプレイしてみてね',
          },
        ],
        buttons: {
          yes: 'もちろん！',
          no: 'やだ',
        },
      },
      crossplay: { text: '購入を完了しよう！' },
      retention: '今ゲームをプレイすると\nぶるぶるが生き返るかも！',

      weeklyLeaderboard: {
        '28h': {
          title: '📢 残り1日！豪華報酬をゲットするチャンス！',
          body: '期限が迫っています！今すぐランクを上げて、限定報酬を手に入れよう！ 🎁⏳',
        },
        '4h': {
          title: '⏳ 残り4時間！最後まで頑張ろう！',
          body: 'ランキングがまもなくリセットされます—今の順位を守ろう！今すぐプレイ！ 🎮🔥',
        },
      },

      guest: {
        dailyBonus: {
          title: 'デイリーボーナス',
          body: '🎁 デイリーボーナスが届いたよ。今すぐ受け取ろう！',
        },
        tournamentLive: {
          title: 'トーナメント',
          body: '🏆 トーナメント開催中！トップを目指そう！',
        },
        tournamentOngoing: {
          title: 'トーナメント',
          body: '🔥 順位が抜かされるかも！トーナメントで防衛しよう！',
        },
        magicLampsStart: {
          title: 'マジックランプ',
          body: '🪔 マジックランプ開催中！賢く選んで大賞を狙おう！',
        },
        magicLampsEnd: {
          title: 'マジックランプ',
          body: '🪔 まもなく終了！魔法が消える前にランプをこすって！',
        },
        treasureExpressLive: {
          title: 'トレジャートレイン',
          body: '🚆 トレジャートレイン出発進行！賞品をゲットしよう！',
        },
        treasureExpressOngoing: {
          title: 'トレジャートレイン',
          body: '⏳ トレジャートレインはまだまだ続くよ！今がチャンス！',
        },
        buruLunch: {
          title: 'ぶるぶるどーぶつ',
          body: '🍎 ぶるぶるが恋しがってる！ランチタイムに会いに来て！',
        },
        buruEvening: {
          title: 'ぶるぶるどーぶつ',
          body: '🌙 ぶるぶるが眠たそう… 寝かしつけに来てあげて！',
        },
        dreamDrift: {
          title: 'おねんねヒーロー',
          body: 'ヒーローを選んでパズルをクリアし、報酬をゲットしよう！',
        },
        weeklyLeaderboards28h: {
          title: '📢 残り1日！豪華報酬をゲットするチャンス！',
          body: '期限が迫っています！今すぐランクを上げて、限定報酬を手に入れよう！ 🎁⏳',
        },
        weeklyLeaderboards4h: {
          title: '⏳ 残り4時間！最後まで頑張ろう！',
          body: 'ランキングがまもなくリセットされます—今の順位を守ろう！今すぐプレイ！ 🎮🔥',
        },
      },
    },
  };
}

export function getLocaleConfig(locale: string) {
  const config = getConfig();
  const lang = config[locale] ? locale : localeRuleset.fallbackLocale;
  return config[lang];
}

export function getPetStatusText(
  locale: Locale,
  status: PetStatusNotification,
) {
  const localeConfig = getLocaleConfig(locale);
  const creative = localeConfig.status[status];

  return creative?.text;
}
